import { useState, useEffect } from 'react';
import { X, Search, Check, Loader2 } from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { User } from '@lms/shared/types';
import { useUsers } from '../hooks/useUsers';
import { useCreateRegistration, useApproveRegistration } from '../hooks/useRegistrations';
import { useNotificationStore } from '../store/notifications';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';

interface ManualEnrollModalProps {
  programId: string;
  onClose: () => void;
}

export function ManualEnrollModal({ programId, onClose }: ManualEnrollModalProps) {
  const { language } = useLanguage();
  const lang = language;
  const t = copy.programDetail;
  const tCommon = copy.common;
  const addNotification = useNotificationStore(state => state.addNotification);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: usersData, isLoading: isLoadingUsers } = useUsers({
    search: debouncedSearch,
    limit: 20,
    excludeProgramId: programId,
  });
  const users = Array.isArray(usersData?.items) ? usersData.items : [];

  const { mutateAsync: createRegistration } = useCreateRegistration();
  const { mutateAsync: approveRegistration } = useApproveRegistration();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleUser = (user: User) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const processUserEnrollment = async (user: User) => {
    const registration = await createRegistration({
      programId,
      email: user.email,
      name: user.name,
      phone: user.phone || undefined,
      gender: user.gender || undefined,
      consentGiven: true,
      domisili: '-',
      reason: 'Manual enrollment by Administrator',
    });

    if (registration && registration.id && registration.status !== 'APPROVED') {
      await approveRegistration({
        id: registration.id,
        adminNotes: 'Manual fast-track approval',
      });
    }
  };

  const CHUNK_SIZE = 5;

  const handleEnroll = async () => {
    if (selectedUsers.length === 0) return;

    setIsSubmitting(true);
    const results: { name: string; ok: boolean }[] = [];

    try {
      // Process in chunks of CHUNK_SIZE to avoid overwhelming the server
      for (let i = 0; i < selectedUsers.length; i += CHUNK_SIZE) {
        const chunk = selectedUsers.slice(i, i + CHUNK_SIZE);
        const chunkResults = await Promise.allSettled(
          chunk.map(user =>
            processUserEnrollment(user).then(() => ({ name: user.name, ok: true }) as const)
          )
        );
        for (const r of chunkResults) {
          if (r.status === 'fulfilled') {
            results.push(r.value);
          } else {
            results.push({ name: 'unknown', ok: false });
          }
        }
      }

      const succeeded = results.filter(r => r.ok).length;
      const failed = results.length - succeeded;

      if (failed === 0) {
        addNotification({
          type: 'success',
          title: t.enrollSuccess[lang],
          message: `${succeeded} peserta telah berhasil didaftarkan.`,
        });
        onClose();
      } else {
        addNotification({
          type: 'warning',
          title: succeeded > 0 ? 'Sebagian berhasil' : 'Gagal',
          message: `${succeeded} berhasil, ${failed} gagal. Silakan coba lagi untuk yang gagal.`,
        });
      }
    } catch (error) {
      console.error('Failed to enroll users:', error);
      addNotification({
        type: 'error',
        title: 'Enrollment Failed',
        message: 'Terdapat kesalahan saat mendaftarkan peserta.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex justify-center p-4 animate-fadeIn overflow-y-auto py-12">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-2xl shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 my-auto flex flex-col max-h-[85vh]">
        <div className="p-8 border-b border-outline-variant/10 bg-surface-container-low/30 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              {t.modalEnrollTitle[lang]}
            </h2>
            <p className="text-xs font-body text-on-surface-variant mt-1">
              {t.modalEnrollDesc[lang]}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors"
          >
            <X size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <div className="p-8 flex-1 overflow-hidden flex flex-col">
          <div className="relative mb-6 shrink-0">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/50"
              size={18}
            />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t.searchUserPlaceholder[lang]}
              className="w-full bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12 pl-12 rounded-xl"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-[250px] border border-outline-variant/20 rounded-xl bg-surface-container-low/10">
            {isLoadingUsers ? (
              <div className="flex flex-col items-center justify-center h-full text-on-surface-variant opacity-60">
                <Loader2 className="animate-spin mb-2" size={24} />
                <span className="text-sm">{tCommon.loading[lang]}</span>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-on-surface-variant opacity-60">
                <span className="text-sm">{t.noUsersFound[lang]}</span>
              </div>
            ) : (
              <div className="divide-y divide-outline-variant/10">
                {users.map((user: User) => {
                  const isSelected = selectedUsers.some(u => u.id === user.id);
                  return (
                    <div
                      key={user.id}
                      onClick={() => toggleUser(user)}
                      className={`p-4 flex items-center justify-between cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary/5 hover:bg-primary/10'
                          : 'hover:bg-surface-container-low'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-primary border-primary text-on-primary'
                              : 'border-on-surface-variant/30'
                          }`}
                        >
                          {isSelected && <Check size={14} strokeWidth={3} />}
                        </div>
                        <div>
                          <p className="font-bold text-on-surface text-sm">{user.name}</p>
                          <p className="text-xs text-on-surface-variant">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-[10px] uppercase font-bold tracking-wider text-on-surface-variant opacity-70">
                        {typeof user.roles?.[0] === 'object' && 'role' in user.roles[0]
                          ? 'Participant'
                          : 'User'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedUsers.length > 0 && (
            <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20 flex flex-wrap gap-2 shrink-0">
              {selectedUsers.map(u => (
                <div
                  key={u.id}
                  className="bg-surface text-on-surface text-xs px-3 py-1.5 rounded-lg border border-outline-variant/20 flex items-center gap-2"
                >
                  <span className="font-medium">{u.name}</span>
                  <button
                    onClick={() => toggleUser(u)}
                    className="hover:text-error transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-outline-variant/10 flex justify-end gap-4 bg-surface-container-lowest shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-bold uppercase tracking-widest text-on-surface-variant hover:text-on-surface transition-colors px-4"
          >
            {tCommon.cancel[lang]}
          </button>
          <Button
            onClick={handleEnroll}
            disabled={selectedUsers.length === 0 || isSubmitting}
            className="rounded-xl px-8"
          >
            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {t.btnEnrollSelected[lang]} ({selectedUsers.length})
          </Button>
        </div>
      </div>
    </div>
  );
}
